import React, { useEffect, useState } from "react";
import { useAppContext } from "../context/AppContext";
import AxiosInstance  from "../context/axiosInstance";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import moment from "moment";
import { useNavigate } from "react-router-dom";

import {
  X,
  Loader2,
  CalendarX,
  ArrowRight,
} from "lucide-react";

const EventListModal = ({ isOpen, onClose }) => {
  const { userID, accessToken } = useAppContext();
 const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);



  useEffect(() => {
    // ✅ 3. Check for 'accessToken' instead of 'token'
    if (isOpen && userID && accessToken) {
      setLoading(true);
      setError(null);

      // ✅ 4. Use the global 'api' instance for the API call
      AxiosInstance.get("/events/", { params: { host_id: userID } })
        .then((res) => {
          setEvents(res.data.events || []);
        })
        .catch((err) => {
          console.error("Error fetching events:", err);
          // The interceptor will handle 401s automatically.
          // We just need a generic message for other errors.
          const errorMsg =
            err.response?.data?.error || "Failed to fetch your events.";
          setError(errorMsg);
          toast.error(errorMsg);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isOpen, userID, accessToken]);

const handleEventClick = (event, isCompleted) => {
  if (isCompleted) {
    toast("This event has already been completed.");
    return;
  }

  // 1. Get the correct event ID. Your backend seems to use `event_id`.
  const eventId = event.event_id || event.id || event._id;

  if (!eventId) {
    toast.error("Could not find event ID to navigate.");
    return;
  }
  // 2. Navigate to the details page, passing the event data in the state.
  navigate(`/app/events/${eventId}`, { state: { event } });



  // 3. Close the modal after triggering the navigation.
  onClose();
};

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-md p-4 transition-all duration-300">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: "circOut" }}
            className="relative w-full max-w-xl bg-surface/90 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl shadow-black/50 p-8 max-h-[85vh] flex flex-col"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors bg-white/5 hover:bg-white/10 rounded-full p-2"
            >
              <X size={20} />
            </button>
            <h2 className="text-2xl font-bold tracking-tighter text-white mb-2">
              My Created Events
            </h2>
            <p className="text-gray-400 text-sm mb-8">
              Manage and track the events you've organized.
            </p>

            <div className="flex-grow overflow-y-auto pr-2 -mr-2 custom-scrollbar">
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div
                    key="loader"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex flex-col items-center justify-center h-48 text-gray-400"
                  >
                    <Loader2 size={32} className="animate-spin mb-4 text-primary-500" />
                    <p className="text-sm font-medium">Finding your events...</p>
                  </motion.div>
                ) : error ? (
                  <motion.div
                    key="error"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex flex-col items-center justify-center h-48 text-red-400 bg-red-500/5 rounded-2xl border border-red-500/20"
                  >
                    <X size={32} className="mb-2" /> 
                    <p className="text-sm font-medium">{error}</p>
                  </motion.div>
                ) : events.length === 0 ? (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex flex-col items-center justify-center h-48 text-gray-400 text-center bg-white/5 rounded-2xl border border-white/5 border-dashed"
                  >
                    <CalendarX size={40} className="mb-4 opacity-50" />
                    <p className="font-bold text-white text-lg">No Events Found</p>
                    <p className="text-sm">You haven't created any events yet.</p>
                  </motion.div>
                ) : (
                  <motion.ul
                    variants={{
                      show: { transition: { staggerChildren: 0.05 } },
                    }}
                    initial="hidden"
                    animate="show"
                    className="space-y-3"
                  >
                    {events.map((event) => {
                      const dt = event.eventDateTime
                        ? new Date(event.eventDateTime)
                        : null;
                      const isCompleted = dt ? dt < new Date() : false;

                      return (
                        <motion.li
                          key={event.event_id || event.id || event._id}
                          layout
                          variants={{
                            hidden: { opacity: 0, y: 10 },
                            show: { opacity: 1, y: 0 },
                          }}
                          onClick={() => handleEventClick(event, isCompleted)}
                          className={`group p-4 rounded-2xl transition-all duration-300 border flex justify-between items-center ${
                            isCompleted
                              ? "bg-white/[0.02] border-white/5 text-gray-500 opacity-60"
                              : "bg-white/5 border-white/5 hover:border-white/20 hover:bg-white/10 cursor-pointer shadow-lg hover:shadow-primary-500/5 hover:scale-[1.01]"
                          }`}
                        >
                          <div className="flex-grow">
                            <h3
                              className={`text-base font-bold tracking-tight ${
                                isCompleted ? "text-gray-400" : "text-white group-hover:text-primary-300"
                              }`}
                            >
                              {event.eventName ||
                                event.title ||
                                "Unnamed Event"}
                            </h3>
                            {dt && (
                              <p className="text-xs text-gray-400 mt-1 flex items-center gap-2">
                                <span className="text-primary-400/80">{moment(dt).format("MMM D, YYYY")}</span>
                                <span className="w-1 h-1 rounded-full bg-gray-600"></span>
                                <span>{moment(dt).format("h:mm A")}</span>
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                            {isCompleted ? (
                              <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-gray-500 bg-gray-500/10 px-2 py-1 rounded-md">
                                COMPLETED
                              </span>
                            ) : (
                              <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-md">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                ACTIVE
                              </span>
                            )}
                            {!isCompleted && (
                              <ArrowRight
                                size={14}
                                className="text-primary-400 opacity-0 group-hover:opacity-100 transition-all transform group-hover:translate-x-1"
                              />
                            )}
                          </div>
                        </motion.li>
                      );
                    })}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EventListModal;
